import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { fetchNewsletterHqSummary, fetchParentOnboardingBoard } from 'api'
import constants from 'app/constants'
import { navigationPaths } from 'app/config'
import { showToast } from 'app/helpers'

// One HQ page, two all-centre views, each with its own sidebar entry (the
// path picks the view): Parent Onboarding and Newsletter. Event Creative and
// Team Roster were dropped from HQ on 2026-10-15 (not relevant at HQ level).
const TABS = [
  {
    key: 'onboarding',
    label: 'Parent Onboarding',
    path: navigationPaths.hqCentreOpsOnboarding
  },
  {
    key: 'newsletter',
    label: 'Newsletter',
    path: navigationPaths.hqCentreOpsNewsletter
  }
]

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  tableWrap: { overflowX: 'auto' },
  empty: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  noneChip: { backgroundColor: '#EF5D34', color: '#fff' },
  finalChip: { backgroundColor: '#E4F2E8', color: '#2E7D32', fontWeight: 600 },
  draftChip: { backgroundColor: '#FFF1E0', color: '#EF6C00', fontWeight: 600 },
  monthCell: { textAlign: 'center', whiteSpace: 'nowrap' },
  monthNone: { color: '#9E9E9E' },
  rollUp: { marginBottom: theme.spacing(3) },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  card: { padding: theme.spacing(1.5, 2) },
  cardWarn: { padding: theme.spacing(1.5, 2), borderColor: '#EF5D34' },
  cardCount: { fontSize: 22, fontWeight: 600 },
  warnText: { color: '#EF5D34', fontWeight: 600 },
  bar: { width: 90, height: 6, borderRadius: 3 },
  percent: { fontSize: 12, minWidth: 32 },
  muted: { color: theme.palette.text.secondary }
}))

// The HQ view is built around what HQ can act on (Satya, 2026-09-29: "needs
// to be action oriented"). Dev data that day: of 711 children the five manual
// steps (orientation, welcome kit, 1:1, WA group, director check-in) had been
// ticked 3 times in all — centres don't use the checklist — so "% of steps"
// read ~13% everywhere and said nothing. The two steps that fill themselves
// (form submitted, app downloaded) are the real signal, and each missing one
// is a parent someone can chase. A family missing both goes to the top: that
// is the call to make first.
const MANUAL_STEPS = [
  'orientation',
  'welcome_kit',
  'family_1on1',
  'whatsapp_group',
  'director_checkin'
]

const stepDone = (r, key) =>
  !!(r.steps || []).find((s) => s.key === key)?.checked

// What a child's parent still owes: 'both' | 'form' | 'app' | null.
const missingOf = (r) => {
  const form = !stepDone(r, 'form_submitted')
  const app = !stepDone(r, 'app_downloaded')
  if (form && app) return 'both'
  if (form) return 'form'
  if (app) return 'app'
  return null
}

const rollUp = (rows) => {
  const byCentre = new Map()
  rows.forEach((r) => {
    const name = r.centre?.name || '—'
    if (!byCentre.has(name))
      byCentre.set(name, {
        centre: name,
        children: 0,
        both: 0,
        formPending: 0,
        appPending: 0,
        manualTicks: 0
      })
    const c = byCentre.get(name)
    c.children += 1
    const m = missingOf(r)
    if (m === 'both') c.both += 1
    if (m === 'both' || m === 'form') c.formPending += 1
    if (m === 'both' || m === 'app') c.appPending += 1
    c.manualTicks += MANUAL_STEPS.filter((k) => stepDone(r, k)).length
  })
  return Array.from(byCentre.values()).sort(
    (a, b) =>
      b.both - a.both ||
      b.formPending + b.appPending - (a.formPending + a.appPending) ||
      a.centre.localeCompare(b.centre)
  )
}

// Academic-year picker, same rules as the centre's own Parent Onboarding page:
// rows synced from enrolments carry their admission's year, form-only and
// hand-added rows have none and group under NO_YEAR. Without it the roll-up
// summed every year at once (prod 2026-10-22: Sarjapur 119 = 74 this year +
// 41 last year + 4 with no year), so HQ never matched the centre's page.
const ALL_YEARS = '__all__'
const NO_YEAR = 'No year'

const worst = (centres, key) =>
  [...centres]
    .filter((c) => c[key] > 0)
    .sort((a, b) => b[key] - a[key])
    .slice(0, 2)
    .map((c) => `${c.centre} ${c[key]}`)
    .join(', ')

const CentreOpsHq = () => {
  const classes = useStyles()
  const location = useLocation()

  const tab =
    TABS.find((t) => location.pathname === t.path)?.key || 'onboarding'

  const [loading, setLoading] = useState(false)

  // Per-tab data caches (fetched on first visit of each tab).
  const [onboardingRows, setOnboardingRows] = useState(null)
  // '' until the rows arrive, then the newest year (see the effect below).
  const [yearFilter, setYearFilter] = useState('')
  const [newsletterRows, setNewsletterRows] = useState(null)
  // One column per month, from the API (the current month last).
  const [newsletterMonths, setNewsletterMonths] = useState([])

  useEffect(() => {
    let active = true
    const load = async () => {
      setLoading(true)
      try {
        if (tab === 'onboarding' && onboardingRows === null) {
          const rows = await fetchParentOnboardingBoard('all')
          if (active) setOnboardingRows(Array.isArray(rows) ? rows : [])
        } else if (tab === 'newsletter' && newsletterRows === null) {
          const data = await fetchNewsletterHqSummary('newsletter')
          if (active) {
            setNewsletterRows(data?.rows || [])
            setNewsletterMonths(data?.months || [])
          }
        }
      } catch (e) {
        showToast('Could not load the HQ view', constants.ERROR)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  // Real years newest-first; NO_YEAR always last so it is never the default.
  const yearOptions = useMemo(
    () =>
      Array.from(
        new Set((onboardingRows || []).map((r) => r.academic_year || NO_YEAR))
      ).sort((a, b) => {
        if (a === NO_YEAR) return 1
        if (b === NO_YEAR) return -1
        return b.localeCompare(a)
      }),
    [onboardingRows]
  )

  // Open on this year's children, not every year at once.
  useEffect(() => {
    if (!yearOptions.length) return
    if (yearFilter === ALL_YEARS || yearOptions.includes(yearFilter)) return
    setYearFilter(yearOptions[0])
  }, [yearOptions, yearFilter])

  // The roll-up covers every centre regardless of the centre filter — filtering
  // it to the centre you already picked would defeat the point of a roll-up.
  // It is scoped to the picked academic year.
  const centreRollUp = useMemo(
    () =>
      rollUp(
        (onboardingRows || []).filter(
          (r) =>
            !yearFilter ||
            yearFilter === ALL_YEARS ||
            (r.academic_year || NO_YEAR) === yearFilter
        )
      ),
    [onboardingRows, yearFilter]
  )

  const totals = useMemo(
    () =>
      centreRollUp.reduce(
        (t, c) => ({
          both: t.both + c.both,
          formPending: t.formPending + c.formPending,
          appPending: t.appPending + c.appPending,
          silent: t.silent + (c.manualTicks ? 0 : 1)
        }),
        { both: 0, formPending: 0, appPending: 0, silent: 0 }
      ),
    [centreRollUp]
  )

  // Month cell: Final (marked final) / Draft (started, not final) / nothing.
  const monthCell = (state) => {
    if (state === 'final')
      return <Chip size="small" label="Final" className={classes.finalChip} />
    if (state === 'draft')
      return <Chip size="small" label="Draft" className={classes.draftChip} />
    return <span className={classes.monthNone}>—</span>
  }

  // Newsletter: Centre + one column per month, Final / Draft / — (2026-10-15).
  const summaryTable = (rows, months) => {
    return (
      <Paper variant="outlined" className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Centre</TableCell>
              {months.map((m) => (
                <TableCell key={m.key} className={classes.monthCell}>
                  {m.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).map((r) => (
              <TableRow key={r.centre_uuid} hover>
                <TableCell>{r.centre}</TableCell>
                {months.map((m) => (
                  <TableCell key={m.key} className={classes.monthCell}>
                    {monthCell((r.by_month || {})[m.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h5">
          {TABS.find((t) => t.key === tab)?.label} — all centres
        </Typography>
        {tab === 'onboarding' && yearOptions.length > 0 && (
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 150 }}
          >
            <InputLabel>Academic year</InputLabel>
            <Select
              label="Academic year"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
              <MenuItem value={ALL_YEARS}>All years</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : tab === 'onboarding' ? (
        <>
          <Box className={classes.cards}>
            {[
              {
                key: 'both',
                icon: '📞',
                count: totals.both,
                text: 'families: no form, no app — call first',
                sub: worst(centreRollUp, 'both')
              },
              {
                key: 'form',
                icon: '📝',
                count: totals.formPending,
                text: 'onboarding forms pending',
                sub: worst(centreRollUp, 'formPending')
              },
              {
                key: 'app',
                icon: '📱',
                count: totals.appPending,
                text: 'parents without the app',
                sub: worst(centreRollUp, 'appPending')
              }
            ].map((card) => (
              <Paper key={card.key} variant="outlined" className={classes.card}>
                <Typography className={classes.cardCount}>
                  {card.icon} {card.count}
                </Typography>
                <Typography>{card.text}</Typography>
                {card.sub && (
                  <Typography variant="body2" className={classes.muted}>
                    worst: {card.sub}
                  </Typography>
                )}
              </Paper>
            ))}
            {totals.silent > 0 && (
              <Paper variant="outlined" className={classes.cardWarn}>
                <Typography className={classes.cardCount}>
                  ⚠️ {totals.silent} of {centreRollUp.length}
                </Typography>
                <Typography>
                  centres have never ticked a checklist step
                </Typography>
                <Typography variant="body2" className={classes.muted}>
                  orientation, welcome kit, 1:1, WA group, check-in — nudge the
                  centre heads
                </Typography>
              </Paper>
            )}
          </Box>

          <Paper
            variant="outlined"
            className={`${classes.tableWrap} ${classes.rollUp}`}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Centre</TableCell>
                  <TableCell align="center">Children</TableCell>
                  <TableCell align="center">Call first</TableCell>
                  <TableCell align="center">Form pending</TableCell>
                  <TableCell align="center">App pending</TableCell>
                  <TableCell align="center">Uses checklist?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {centreRollUp.map((c) => (
                  <TableRow key={c.centre} hover>
                    <TableCell>{c.centre}</TableCell>
                    <TableCell align="center">{c.children}</TableCell>
                    <TableCell align="center">
                      {c.both ? (
                        <Chip
                          size="small"
                          label={c.both}
                          className={classes.noneChip}
                        />
                      ) : (
                        <span className={classes.muted}>0</span>
                      )}
                    </TableCell>
                    <TableCell align="center">{c.formPending}</TableCell>
                    <TableCell align="center">{c.appPending}</TableCell>
                    <TableCell align="center">
                      {c.manualTicks ? (
                        'yes'
                      ) : (
                        <span className={classes.warnText}>no</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!centreRollUp.length && (
                  <TableRow>
                    <TableCell colSpan={6} className={classes.empty}>
                      No onboarding rows yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      ) : (
        summaryTable(newsletterRows, newsletterMonths)
      )}
    </Box>
  )
}

export default CentreOpsHq
